import {
    useEffect,
    type FC
} from "react";
import {
    Platform,
    View
} from "react-native";
import type IFieldErrorProps from "./type";
import stylesheet, {
    FIELD_ERROR_VARIANT,
    FIELD_ERROR_BULLET,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useFieldOutsideWarning,
    useFieldContext
} from "../../context";
import {
    CircleAlert as CircleAlertIcon
} from "lucide-react-native";
import {
    getFieldErrorMessages
} from "../../util";
import Text from "../../../text";

const FieldError: FC<IFieldErrorProps> = ({
    customTheme: customThemeProp,
    children,
    errors,
    style,
    ...props
}) => {
    const context = useFieldContext();

    useFieldOutsideWarning({
        isInside: !!context,
        parent: "Field",
        part: "Error"
    });

    const customTheme = customThemeProp ?? context?.customTheme;

    const {
        typography,
        iconSizes,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        container: containerDynamicStyle,
        icon: iconDynamicStyle,
        row: rowDynamicStyle
    } = useStyles({
        typography,
        spaces
    });

    const messages = getFieldErrorMessages({
        children,
        errors
    });

    const messagesKey = messages.join("\n");

    const text = messages.join(". ");

    const hasMessages = messages.length > 0;

    const registerPart = context?.registerPart;

    const webProps = Platform.OS === "web" ? {
        "aria-live": "polite" as const
    } : {};

    useEffect(() => {
        if(!registerPart || !hasMessages) {
            return;
        }

        return registerPart("error", text);
    }, [
        hasMessages,
        registerPart,
        text
    ]);

    if(!hasMessages) {
        return null;
    }

    const color = colors.content.text.danger;

    if(messages.length === 1) {
        return <View
            {...props}
            {...webProps}
            id={context?.errorID}
            style={[
                style,
                stylesheet.row,
                rowDynamicStyle
            ]}
        >
            <View
                style={[
                    stylesheet.icon,
                    iconDynamicStyle
                ]}
                aria-hidden={true}
            >
                <CircleAlertIcon
                    size={iconSizes.small}
                    color={color}
                />
            </View>
            <Text
                variant={FIELD_ERROR_VARIANT}
                style={stylesheet.message}
                customTheme={customTheme}
                customColor={color}
            >
                {messages[0]}
            </Text>
        </View>;
    }

    return <View
        {...props}
        {...webProps}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
        id={context?.errorID}
        role="list"
    >
        {
            messages.map((message, index) => <View
                key={`${messagesKey}-${index}`}
                style={[
                    stylesheet.row,
                    rowDynamicStyle
                ]}
                role="listitem"
            >
                <Text
                    variant={FIELD_ERROR_VARIANT}
                    customTheme={customTheme}
                    customColor={color}
                    aria-hidden={true}
                >
                    {FIELD_ERROR_BULLET}
                </Text>
                <Text
                    variant={FIELD_ERROR_VARIANT}
                    style={stylesheet.message}
                    customTheme={customTheme}
                    customColor={color}
                >
                    {message}
                </Text>
            </View>)
        }
    </View>;
};
export default FieldError;
